import { useMemo, useState } from "react";
import { GRADE_GROUPS } from "@/lib/subjects";
import { Users, UserRound } from "lucide-react";

const sel = "rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

// Sınıf + Şube filtreli ortak öğrenci seçici — tüm admin/koç panellerinde kullanılır
export function ClassStudentSelect({ students, value, onChange, testid, placeholder = "Öğrenci seçin…" }) {
  const [grade, setGrade] = useState(0);
  const [section, setSection] = useState("");
  const grades = useMemo(() => [...new Set(students.map((s) => s.grade).filter(Boolean))].sort((a, b) => a - b), [students]);
  const sections = useMemo(() => [...new Set(students.filter((s) => !grade || s.grade === grade).map((s) => s.section).filter(Boolean))].sort(), [students, grade]);
  const filtered = students.filter((s) => (!grade || s.grade === grade) && (!section || s.section === section));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select data-testid={`${testid}-grade`} value={grade} onChange={(e) => setGrade(Number(e.target.value))} className={sel} title="Sınıf filtresi">
        <option value={0}>Tüm Sınıflar</option>
        {grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
      </select>
      <select data-testid={`${testid}-section`} value={section} onChange={(e) => setSection(e.target.value)} className={sel} title="Şube filtresi">
        <option value="">Tüm Şubeler</option>
        {sections.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value || "")} className={`${sel} min-w-52 flex-1`}>
        <option value="">{placeholder}</option>
        {filtered.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.grade}. Sınıf{s.section ? `/${s.section}` : ""})</option>)}
      </select>
    </div>
  );
}

// Tek Öğrenci / Tüm Sınıf hedef seçici — veri giriş panellerinde ortak kullanım
export function TargetModeSelect({ students, mode, setMode, studentId, setStudentId, grade, setGrade, section, setSection, testid, studentPlaceholder = "Öğrenci seçin…" }) {
  const classStudents = students.filter((s) => s.grade === grade && (!section || s.section === section));
  const clsSections = [...new Set(students.filter((s) => s.grade === grade).map((s) => s.section).filter(Boolean))].sort();
  const selCls = "rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-slate-700">Hedef Seçimi</span>
        <div className="flex rounded-xl bg-slate-100 p-1">
          <button type="button" data-testid={`${testid}-target-student`} onClick={() => setMode("student")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${mode === "student" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
            <UserRound className="h-4 w-4" /> Tek Öğrenci
          </button>
          <button type="button" data-testid={`${testid}-target-class`} onClick={() => setMode("class")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${mode === "class" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
            <Users className="h-4 w-4" /> Tüm Sınıf
          </button>
        </div>
      </div>
      {mode === "student" ? (
        <ClassStudentSelect students={students} value={studentId} onChange={(v) => setStudentId(v || "")} testid={testid} placeholder={studentPlaceholder} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid={`${testid}-class-grade`} value={grade} onChange={(e) => setGrade(Number(e.target.value))} className={selCls}>
            {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
          </select>
          <select data-testid={`${testid}-class-section`} value={section} onChange={(e) => setSection(e.target.value)} className={selCls}>
            <option value="">Tüm Şubeler</option>
            {clsSections.map((s) => <option key={s} value={s}>{s} Şubesi</option>)}
          </select>
          <span data-testid={`${testid}-class-chip`} className="flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700">
            <Users className="h-4 w-4" /> {grade}. Sınıf{section ? `/${section}` : " (tüm şubeler)"} · {classStudents.length} öğrenci
          </span>
        </div>
      )}
    </div>
  );
}
